/**
 * Pure status-page model: maps safe health payloads → UI states.
 * No secrets, no admin metrics — public SaaS status pattern (GitHub/Stripe style).
 */

export type ComponentState = "operational" | "degraded" | "outage" | "unknown";
export type OverallState = "operational" | "degraded" | "outage";

interface HealthCheckEntry {
  status: string;
  latencyMs?: number;
  error?: string;
}

export interface HealthPayload {
  status?: string;
  timestamp?: string;
  latencyMs?: number;
  checks?: Record<string, HealthCheckEntry>;
  // 2026-08-24 infra-scale — optional free-tier quota glimpse for /api/health consumers (banner + status page)
  infra?: {
    vercelHobby?: { fastDataTransferGB: number };
    supabaseFree?: { databaseMB: number; autoPauseDays: number };
    tavily?: { pooledCredits: number; used: number; exhausted: boolean };
    // 2026-09-12 cadena Tavily→Parallel→You: vista combinada normalizada
    // a búsquedas (aditiva — tavily intacto para compatibilidad).
    searchPool?: {
      used: number;
      pooled: number;
      ratio: number;
      exhausted: boolean;
      warn: boolean;
      saveMode: boolean;
    };
    guidance?: string;
  };
  overload?: {
    phase: string;
    confident: boolean;
    reasons: string[];
    degrade: Record<string, boolean>;
    retryAfterSeconds: number;
  };
}

interface StatusComponent {
  id: string;
  /** Translation key for the component name */
  nameKey: StatusNameKey;
  /** Optional description key */
  descriptionKey: StatusDescKey;
  state: ComponentState;
  latencyMs?: number;
}

type StatusNameKey =
  | "statusCompWeb"
  | "statusCompApi"
  | "statusCompDatabase"
  | "statusCompChat"
  | "statusCompImages"
  | "statusCompResearch"
  | "statusCompStudy"
  | "statusCompPayments";

type StatusDescKey =
  | "statusCompWebDesc"
  | "statusCompApiDesc"
  | "statusCompDatabaseDesc"
  | "statusCompChatDesc"
  | "statusCompImagesDesc"
  | "statusCompResearchDesc"
  | "statusCompStudyDesc"
  | "statusCompPaymentsDesc";

export interface StatusPageModel {
  overall: OverallState;
  components: StatusComponent[];
  checkedAt: string;
  apiLatencyMs?: number;
}

function mapCheckStatus(raw: string | undefined): ComponentState {
  if (!raw) return "unknown";
  const s = raw.toLowerCase();
  if (s === "healthy" || s === "configured" || s === "ok" || s === "up") {
    return "operational";
  }
  if (s === "degraded" || s === "partial") return "degraded";
  if (s === "unhealthy" || s === "down" || s === "outage" || s === "error") {
    return "outage";
  }
  return "unknown";
}

function mapOverall(
  raw: string | undefined,
  components: StatusComponent[],
): OverallState {
  const fromPayload = mapCheckStatus(raw);
  if (fromPayload === "outage") return "outage";
  if (fromPayload === "degraded") return "degraded";
  if (fromPayload === "operational") {
    // Still respect worst component (e.g. future multi-check payloads)
    if (components.some((c) => c.state === "outage")) return "outage";
    if (
      components.some((c) => c.state === "degraded" || c.state === "unknown")
    ) {
      return "degraded";
    }
    return "operational";
  }
  if (components.some((c) => c.state === "outage")) return "outage";
  if (components.some((c) => c.state === "degraded" || c.state === "unknown")) {
    return "degraded";
  }
  return "operational";
}

/**
 * Build a public status model from `/api/health` JSON (or equivalent).
 * Web app is treated operational when the payload itself is reachable.
 *
 * 2026-09-27 honest-status: each component prefers its own probe entry from
 * runHealthChecks (chat/research = managed pool gauges, study = same Postgres
 * store as database, payments = wompi_events 24h ledger). Entries absent in
 * older payloads fall back to the legacy mirror (no behavior change there).
 * images has NO independent probe — generation runs in this same API
 * deployment, so API reachability is its signal; a Cloudflare outage surfaces
 * as per-request generation errors, not as state. The fallback is documented
 * here instead of fabricating per-component health.
 */
export function mapHealthToStatusPage(
  payload: HealthPayload | null | undefined,
  options?: { fetchFailed?: boolean; now?: string },
): StatusPageModel {
  const now = options?.now ?? new Date().toISOString();

  if (options?.fetchFailed || !payload) {
    return {
      overall: "outage",
      checkedAt: now,
      components: [
        {
          id: "web",
          nameKey: "statusCompWeb",
          descriptionKey: "statusCompWebDesc",
          state: "outage",
        },
        {
          id: "api",
          nameKey: "statusCompApi",
          descriptionKey: "statusCompApiDesc",
          state: "outage",
        },
        {
          id: "database",
          nameKey: "statusCompDatabase",
          descriptionKey: "statusCompDatabaseDesc",
          state: "unknown",
        },
        {
          id: "chat",
          nameKey: "statusCompChat",
          descriptionKey: "statusCompChatDesc",
          state: "unknown",
        },
        {
          id: "images",
          nameKey: "statusCompImages",
          descriptionKey: "statusCompImagesDesc",
          state: "unknown",
        },
        {
          id: "research",
          nameKey: "statusCompResearch",
          descriptionKey: "statusCompResearchDesc",
          state: "unknown",
        },
        {
          id: "study",
          nameKey: "statusCompStudy",
          descriptionKey: "statusCompStudyDesc",
          state: "unknown",
        },
        {
          id: "payments",
          nameKey: "statusCompPayments",
          descriptionKey: "statusCompPaymentsDesc",
          state: "unknown",
        },
      ],
    };
  }

  const supabase = payload.checks?.supabase;
  const dbState = mapCheckStatus(supabase?.status);
  const apiState = mapCheckStatus(payload.status);
  // 2026-09-27 honest-status: own probe entry wins; missing entry (older
  // payloads) keeps the legacy mirror so historic snapshots render unchanged.
  const probed = (id: string): ComponentState | null => {
    const raw = payload.checks?.[id]?.status;
    return raw === undefined ? null : mapCheckStatus(raw);
  };
  // If health endpoint answered, API + web are at least reachable
  const webState: ComponentState =
    apiState === "outage"
      ? "degraded"
      : apiState === "unknown"
        ? "operational"
        : "operational";
  const coreState: ComponentState =
    apiState === "outage"
      ? "outage"
      : apiState === "degraded"
        ? "degraded"
        : "operational";
  const chatState: ComponentState = probed("chat") ?? coreState;
  // Research degrades if Tavily pool exhausted (honest infra signal)
  const tavilyExhausted = payload.infra?.tavily?.exhausted === true;
  const legacyResearch: ComponentState = tavilyExhausted
    ? "degraded"
    : coreState === "outage"
      ? "outage"
      : coreState === "degraded"
        ? "degraded"
        : "operational";
  const researchState: ComponentState = probed("research") ?? legacyResearch;
  // images: no independent probe (see docblock) — API reachability is the signal.
  const imagesState: ComponentState = probed("images") ?? coreState;
  const studyState: ComponentState = probed("study") ?? coreState;
  const paymentsState: ComponentState = probed("payments") ?? coreState;

  const components: StatusComponent[] = [
    {
      id: "web",
      nameKey: "statusCompWeb",
      descriptionKey: "statusCompWebDesc",
      state: webState,
    },
    {
      id: "api",
      nameKey: "statusCompApi",
      descriptionKey: "statusCompApiDesc",
      state: coreState,
      latencyMs: payload.latencyMs,
    },
    {
      id: "database",
      nameKey: "statusCompDatabase",
      descriptionKey: "statusCompDatabaseDesc",
      state: dbState,
      latencyMs: supabase?.latencyMs,
    },
    {
      id: "chat",
      nameKey: "statusCompChat",
      descriptionKey: "statusCompChatDesc",
      state: chatState,
    },
    {
      id: "images",
      nameKey: "statusCompImages",
      descriptionKey: "statusCompImagesDesc",
      state: imagesState,
    },
    {
      id: "research",
      nameKey: "statusCompResearch",
      descriptionKey: "statusCompResearchDesc",
      state: researchState,
    },
    {
      id: "study",
      nameKey: "statusCompStudy",
      descriptionKey: "statusCompStudyDesc",
      state: studyState,
    },
    {
      id: "payments",
      nameKey: "statusCompPayments",
      descriptionKey: "statusCompPaymentsDesc",
      state: paymentsState,
    },
  ];

  return {
    overall: mapOverall(payload.status, components),
    components,
    checkedAt: payload.timestamp ?? now,
    apiLatencyMs: payload.latencyMs,
  };
}

/**
 * Lightweight poll payload from `GET /api/infra-status` (in-memory snapshot,
 * `max-age=60`) — only the overload phase, no per-component checks.
 */
export interface InfraStatusPayload {
  phase?: string;
  updatedAt?: string;
}

/**
 * 2026-09-18 FASE 2 perf (free-tier CPU budget): aplica la fase de
 * `/api/infra-status` sobre el modelo vigente. Solo mueve `overall` +
 * `checkedAt`; el detalle por componente viene del SSR inicial
 * (runHealthChecks) y no se inventa desde un snapshot sin checks.
 * Fase desconocida/ausente → devuelve `prev` sin cambios (no flapping).
 */
export function applyInfraPhase(
  prev: StatusPageModel,
  payload: InfraStatusPayload | null | undefined,
  options?: { now?: string },
): StatusPageModel {
  const now = options?.now ?? new Date().toISOString();
  const phase =
    typeof payload?.phase === "string" ? payload.phase.toLowerCase() : "";
  if (phase === "normal") {
    return {
      ...prev,
      overall: "operational",
      checkedAt: payload?.updatedAt ?? now,
    };
  }
  if (phase === "warm" || phase === "hot") {
    return {
      ...prev,
      overall: "degraded",
      checkedAt: payload?.updatedAt ?? now,
    };
  }
  if (phase === "critical" || phase === "emergency") {
    return { ...prev, overall: "outage", checkedAt: payload?.updatedAt ?? now };
  }
  return prev;
}

export function overallStateLabelKey(
  state: OverallState,
):
  "statusOverallOperational" | "statusOverallDegraded" | "statusOverallOutage" {
  if (state === "operational") return "statusOverallOperational";
  if (state === "degraded") return "statusOverallDegraded";
  return "statusOverallOutage";
}

export function componentStateLabelKey(
  state: ComponentState,
):
  | "statusStateOperational"
  | "statusStateDegraded"
  | "statusStateOutage"
  | "statusStateUnknown" {
  if (state === "operational") return "statusStateOperational";
  if (state === "degraded") return "statusStateDegraded";
  if (state === "outage") return "statusStateOutage";
  return "statusStateUnknown";
}
